import { FeatureSet } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import { getAuth } from '@/hooks/useAuth';

/** Recursion depth limiter. */
type Prev = [never, 0, 1, 2, 3, 4];

/**
 * Utility type to create a union of valid dot‑separated paths.
 * The extra type parameter D limits the recursion depth.
 */
type FeaturePaths<T, D extends number = 5> = [D] extends [0]
  ? never
  : {
      [K in keyof T & string]: NonNullable<T[K]> extends { enabled?: boolean }
        ? // Include the key itself, plus any nested paths (if any)
            | K
            | `${K}.${FeaturePaths<
                Omit<NonNullable<T[K]>, 'enabled'>,
                Prev[D]
              >}`
        : never;
    }[keyof T & string];

/** Set of all possible feature paths. */
export type FeaturePath = FeaturePaths<FeatureSet>;

/** Get if a feature is enabled. */
const getEnabled = (featureSet: FeatureSet, path: FeaturePath): boolean => {
  const keys = path.split('.');
  let current: Record<string, unknown> = featureSet;
  for (const key of keys) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (current === undefined) {
      return false;
    }
    current = current[key] as Record<string, unknown>;
  }
  return Boolean((current as Record<string, unknown> | undefined)?.enabled);
};

/** Get if a user has permission to a feature. */
export const getFeaturePermission = (path: FeaturePath) => {
  const auth = getAuth();
  const featureSet = auth?.features ?? {};
  return getEnabled(featureSet, path);
};

/**
 * Get if a user has permission to a feature.
 *
 * @param path The path to the feature in the feature set.
 * @returns If the feature is enabled.
 */
export const useFeaturePermission = (path: FeaturePath) => {
  const { data: auth } = useAuth();
  const features = auth?.features ?? {};
  return getEnabled(features, path);
};
